import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import _ from 'lodash';
import { StudentData } from '../utils/types';

interface AnalyticsProps {
  data: StudentData[];
  darkMode?: boolean;
}

const Analytics: React.FC<AnalyticsProps> = ({ data, darkMode = false }) => {
  // GPA Distribution
  const gpaData = [
    { name: '3.5-4.0', value: data.filter(s => s.gpa >= 3.5).length },
    { name: '3.0-3.49', value: data.filter(s => s.gpa >= 3.0 && s.gpa < 3.5).length },
    { name: '2.5-2.99', value: data.filter(s => s.gpa >= 2.5 && s.gpa < 3.0).length },
    { name: '2.0-2.49', value: data.filter(s => s.gpa >= 2.0 && s.gpa < 2.5).length },
    { name: '< 2.0', value: data.filter(s => s.gpa < 2.0).length }
  ];

  // Major Distribution
  const majorData = Object.entries(
    _.countBy(data, 'major')
  )
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // University Distribution
  const universityData = Object.entries(
    _.countBy(data, 'university')
  )
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
    '#FF6666', '#66FF66', '#6666FF', '#FF33CC', '#33FFF6',
    '#B266FF', '#FFB266', '#B2FF66', '#66FFB2', '#FF6699'
  ];

  const chartTheme = darkMode ? {
    backgroundColor: '#1f2937',
    textColor: '#ffffff',
    axisColor: '#4b5563',
  } : {
    backgroundColor: '#ffffff',
    textColor: '#000000',
    axisColor: '#e5e7eb',
  };

  // Calculate average GPA
  const avgGpa = data.length > 0 
    ? (data.reduce((sum, student) => sum + student.gpa, 0) / data.length).toFixed(2)
    : '0.00';

  return (
    <div className={`p-4 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <h2 className="text-xl font-bold mb-4">📊 Analytics Dashboard</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* GPA Distribution */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-black'}`}>GPA Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={gpaData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                outerRadius={80}
                dataKey="value"
              >
                {gpaData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Major Distribution */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-black'}`}>Major Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={majorData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={false} />
              <YAxis />
              <Tooltip formatter={(value, name, props) => [value, props.payload.name]} />
              <Bar dataKey="value">
                {majorData.map((entry, index) => (
                  <Cell
                    key={`cell-major-${index}`}
                    fill={entry.name === 'Computer Science' ? '#FFA500' : '#8884d8'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* University Distribution */}
        <div className={`p-4 rounded-lg shadow col-span-2 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-black'}`}>University Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={universityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={false} />
              <YAxis />
              <Tooltip formatter={(value, name, props) => [value, props.payload.name]} />
              <Bar dataKey="value">
                {universityData.map((entry, index) => (
                  <Cell
                    key={`cell-university-${index}`}
                    fill={index === 0 ? '#FF0000' : '#8884d8'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Statistics */}
        <div className={`shadow rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-black'}`}>
            Summary Statistics
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-sm" style={{ color: chartTheme.textColor }}>
                Total Students: {data.length}
              </p>
              <p className="text-sm" style={{ color: chartTheme.textColor }}>
                Avg GPA: {avgGpa}
              </p>
            </div>
            <div>
              <p className="text-sm" style={{ color: chartTheme.textColor }}>
                Top Major: {
                  majorData.length > 0 
                    ? majorData.reduce((a, b) => a.value > b.value ? a : b).name 
                    : 'N/A'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;